import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';

export type KeyValueRowProps = {
  label: ReactNode;
  value: ReactNode;
  leading?: ReactNode;
  className?: string;
  labelClassName?: string;
  valueClassName?: string;
  /**
   * How the label overflows. Default `1` = single-line ellipsis so the value
   * stays fully visible. Use `2` for offer titles that may wrap once.
   */
  labelLines?: 1 | 2;
  align?: 'center' | 'start';
  'data-testid'?: string;
};

export const KeyValueRow = ({
  label,
  value,
  leading,
  className,
  labelClassName,
  valueClassName,
  labelLines = 1,
  align = 'center',
  'data-testid': dataTestId,
}: KeyValueRowProps) => {
  return (
    <div
      data-testid={dataTestId}
      className={cn(
        'flex min-w-0 justify-between gap-2',
        align === 'start' ? 'items-start' : 'items-center',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {leading != null && <div className="shrink-0">{leading}</div>}
        <div
          title={typeof label === 'string' ? label : undefined}
          className={cn('min-w-0 flex-1', labelLines === 2 ? 'line-clamp-2 break-words' : 'truncate', labelClassName)}
        >
          {label}
        </div>
      </div>
      <div className={cn('shrink-0 whitespace-nowrap', valueClassName)}>{value}</div>
    </div>
  );
};
